import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');

  const navigationItems = [
    { id: 'hero', label: 'Accueil' },
    { id: 'about', label: 'A propos' },
    { id: 'schedule', label: 'Planning' },
    { id: 'pricing', label: 'Tarifs' },
    { id: 'location', label: 'Localisation' },
    { id: 'gallery', label: 'Galerie' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const sections = navigationItems.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(navigationItems[i].id);
          break;
        }
      }
    };

    // Handle direct anchor links
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && navigationItems.some(item => item.id === hash)) {
        setTimeout(() => {
          scrollToSection(hash);
        }, 100);
      }
    };

    // Check for hash on initial load
    const initialHash = window.location.hash.replace('#', '');
    if (initialHash && navigationItems.some(item => item.id === initialHash)) {
      setTimeout(() => {
        scrollToSection(initialHash);
      }, 500); // Longer delay for initial load
    }

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 100; // Increased to account for actual header + padding
      
      // Clear any existing hash to prevent default anchor behavior
      if (window.location.hash) {
        window.history.replaceState(null, null, window.location.pathname);
      }
      
      // Use offsetTop for more reliable positioning
      const elementPosition = element.offsetTop - headerHeight;
      const targetPosition = Math.max(0, elementPosition);
      const startPosition = window.pageYOffset;
      const distance = targetPosition - startPosition;
      const duration = 800; // ms
      let startTime = null;

      // Custom smooth scroll animation that can't be interrupted
      const animateScroll = (currentTime) => {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);
        
        // Easing function for smooth animation
        const ease = (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        const easedProgress = ease(progress);
        
        const currentPosition = startPosition + (distance * easedProgress);
        window.scrollTo(0, currentPosition);
        
        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };
      
      requestAnimationFrame(animateScroll);
      setIsMenuOpen(false);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-opacity-95 backdrop-blur-sm" 
            style={{ backgroundColor: 'rgba(47, 47, 47, 0.95)' }}>
      <div className="container px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center py-4 relative" style={{ minHeight: '60px' }}>
          {/* Logo */}
          <div className="flex items-center pr-4 xl:pr-0">
            <img 
              src="/favicons/192x192.png" 
              alt="Logo Cercle Cadurcien d'Aïkido" 
              className="w-8 h-8 sm:w-10 sm:h-10 mr-2 sm:mr-3 flex-shrink-0"
            />
            <div className="flex flex-col justify-center leading-none">
              <h1 className="text-xs sm:text-sm lg:text-base font-bold text-primary uppercase tracking-wide"
                  style={{ color: 'var(--brand-primary)' }}>
                <span className="hidden sm:inline">Cercle Cadurcien</span>
                <span className="sm:hidden">CCA</span>
              </h1>
              <h2 className="text-xs sm:text-sm lg:text-base font-bold text-primary uppercase tracking-wide -mt-0.5"
                  style={{ color: 'var(--brand-primary)' }}>
                <span className="hidden sm:inline">d'Aïkido</span>
              </h2>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center space-x-2 ml-auto">
            {navigationItems.map((item) => (
              <button
                key={item.id}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent focus issues
                  scrollToSection(item.id);
                }}
                className={`nav-link ${activeSection === item.id ? 'active' : ''}`}
              >
                {item.label}
              </button>
            ))}
            <button 
              className="btn-primary ml-4"
              onMouseDown={(e) => {
                e.preventDefault();
                scrollToSection('pricing');
              }}
            >
              Essai Gratuit 
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="xl:hidden p-2 absolute right-0 top-1/2 transform -translate-y-1/2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            style={{ 
              color: 'var(--brand-primary)',
              width: '48px',
              height: '48px',
              zIndex: 10
            }}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="xl:hidden bg-opacity-98 backdrop-blur-sm border-t"
               style={{ backgroundColor: 'rgb(47, 47, 47)', borderColor: 'var(--border-medium)' }}>
            <div className="py-4 space-y-2">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    scrollToSection(item.id);
                  }}
                  className={`nav-link block w-full text-left ${activeSection === item.id ? 'active' : ''}`}
                >
                  {item.label}
                </button>
              ))}
              <button 
                className="btn-primary w-full mt-4"
                onMouseDown={(e) => {
                  e.preventDefault();
                  scrollToSection('pricing');
                }}
              >
                Free Trial
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
